import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ArrowLeft, Copy, ChevronDown, ChevronUp, WifiOff, Lock, CreditCard, SearchX } from 'lucide-react';
import { AppError, NetworkError, AuthError, PaymentError, NotFoundError, handleError } from '../lib/errors';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  retryCount: number;
}

const MAX_RETRIES = 3;

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    handleError(error, { componentStack: errorInfo.componentStack ?? undefined });
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  private handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const text = `Error: ${error?.message}\n\nStack: ${error?.stack}\n\nComponent Stack: ${errorInfo?.componentStack}`;
    navigator.clipboard.writeText(text);
  };

  private getErrorDisplay() {
    const { error } = this.state;
    if (error instanceof NetworkError) {
      return {
        icon: <WifiOff className="w-8 h-8 text-orange-500" />,
        iconBg: 'bg-orange-100 dark:bg-orange-900/30',
        title: 'Connection Problem',
        message: 'Unable to reach our servers. Please check your internet connection and try again.',
      };
    }
    if (error instanceof AuthError) {
      return {
        icon: <Lock className="w-8 h-8 text-yellow-500" />,
        iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
        title: 'Authentication Required',
        message: 'Your session may have expired. Please sign in again to continue.',
      };
    }
    if (error instanceof PaymentError) {
      return {
        icon: <CreditCard className="w-8 h-8 text-purple-500" />,
        iconBg: 'bg-purple-100 dark:bg-purple-900/30',
        title: 'Payment Issue',
        message: 'There was a problem processing your payment. Please try again or contact support.',
      };
    }
    if (error instanceof NotFoundError) {
      return {
        icon: <SearchX className="w-8 h-8 text-blue-500" />,
        iconBg: 'bg-blue-100 dark:bg-blue-900/30',
        title: 'Not Found',
        message: 'The page or resource you were looking for could not be found.',
      };
    }
    return {
      icon: <AlertTriangle className="w-8 h-8 text-red-500" />,
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      title: 'Oops! Something went wrong',
      message: 'We\'re sorry for the inconvenience. Please try again or return to the home page.',
    };
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const canRetry = this.state.retryCount < MAX_RETRIES;
      const display = this.getErrorDisplay();

      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <div className={`w-16 h-16 ${display.iconBg} rounded-full flex items-center justify-center mx-auto mb-5`}>
                {display.icon}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {display.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {display.message}
              </p>

              <div className="space-y-3">
                {canRetry && (
                  <button
                    onClick={this.handleRetry}
                    className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors w-full font-medium"
                  >
                    <RefreshCw className="w-5 h-5 mr-2" />
                    Try Again {this.state.retryCount > 0 && `(${MAX_RETRIES - this.state.retryCount} left)`}
                  </button>
                )}
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors w-full font-medium"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Refresh Page
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium py-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Return to Home
                </Link>
              </div>

              {/* Error details toggle (dev-friendly) */}
              {this.state.error && (
                <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <button
                    onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
                    className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    {this.state.showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    Technical Details
                  </button>
                  {this.state.showDetails && (
                    <div className="mt-3 text-left">
                      <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-3 text-xs font-mono text-gray-700 dark:text-gray-300 max-h-40 overflow-auto">
                        {this.state.error instanceof AppError && (
                          <div className="mb-1 text-gray-500">Code: {this.state.error.code}{this.state.error.statusCode ? ` (${this.state.error.statusCode})` : ''}</div>
                        )}
                        {this.state.error.message}
                      </div>
                      <button
                        onClick={this.handleCopyError}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        <Copy className="w-3 h-3" />
                        Copy error details
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;